// The debrief narrative prompt. The model is the rapporteur and nothing else: it writes only from the
// facts this file hands it — the decision log, the lost-time list, the run statistics and a summary of
// the order log — and cites the event ids each paragraph rests on. It never adjudicates, never invents
// a contact, and never reaches for anything outside the listed facts. See docs/design/ai-patterns.md
// §5 "AI as rapporteur" and spec §6.

import { TICKS_PER_MIN } from '../sim/types';
import type { Event, Order } from '../sim/types';
import type { DebriefContext } from './templates';

/** Sim ticks as whole minutes on the H-hour clock, the only time unit the model is given. */
const min = (tick: number): number => Math.round(tick / TICKS_PER_MIN);

export const DEBRIEF_SYSTEM = [
  'You are the rapporteur at the after-action review of a battalion task force attack.',
  'Write only from the listed facts. Do not invent units, contacts, times, or outcomes, and do not',
  'speculate beyond what the facts state. Cite the event ids each paragraph rests on in',
  'cited_event_ids; every id you cite must appear in the facts. Be specific about where time was lost',
  'and about what the player could have done instead, naming the callsign, the order card and the',
  'minute. Write three to five paragraphs of plain prose, no bullet lists, no headings inside the text.',
  'Each paragraph gets a short heading of two or three words.',
  // The length rule is in the schema as well (`DebriefSchema`), and a paragraph over the cap throws
  // the whole narrative away for a template — so the model is told the number rather than left to
  // guess at what "a paragraph" means. Measured before this line went in: every run overran.
  'Keep each paragraph under 900 characters; a long one is dropped and the reader gets nothing.',
].join(' ');

/** The compact JSON the model sees, and the event ids it is allowed to cite. */
export interface DebriefPrompt {
  system: string;
  user: string;
  /** Ids named in the facts; `validateCitations` drops anything outside this set. */
  knownEventIds: Set<number>;
}

interface Facts {
  outcome: string;
  durationMin: number;
  stats: Record<string, number>;
  decisions: {
    id: number;
    min: number;
    side: 'blue' | 'red';
    event: string;
    blueOrdersWithin5Min: string[];
    redDecisionsWithin5Min: string[];
  }[];
  lostTime: { item: string; minutes: number; eventIds: number[] }[];
  orderSummary: { total: number; byCard: Record<string, number>; firstOrderMin: number | null; lastOrderMin: number | null };
}

/**
 * Builds the rapporteur prompt from an already-computed debrief context. `decisions` and `lostTimeFull`
 * come from `src/debrief/analyze.ts`; `DebriefContext` itself is the same object the template fallback
 * takes, so the two paths are always grounded in the same facts.
 */
export function buildDebriefPrompt(
  ctx: DebriefContext,
  extra: {
    outcome: string;
    durationMin: number;
    decisions: { event: Event; blueOrders: Order[]; redDecisions: Event[] }[];
    lostTimeFull: { item: string; minutes: number; eventIds: number[] }[];
    eventLine: (ev: Event) => string;
    cardLabel: (card: string) => string;
  },
): DebriefPrompt {
  const callsign = new Map(ctx.units.map((u) => [u.id, u.callsign]));
  const known = new Set<number>();

  // The decision log is the spine of the narrative; it is capped so a long run still fits one request.
  const decisions: Facts['decisions'] = extra.decisions.slice(0, 40).map((d) => {
    known.add(d.event.id);
    for (const r of d.redDecisions) known.add(r.id);
    return {
      id: d.event.id,
      min: min(d.event.t),
      side: d.event.side === 1 ? 'red' : 'blue',
      event: extra.eventLine(d.event),
      blueOrdersWithin5Min: d.blueOrders.map(
        (o) => `H+${min(o.issuedAt)}m ${callsign.get(o.unitId) ?? `unit ${o.unitId}`} ${extra.cardLabel(o.card)} (${o.status})`,
      ),
      redDecisionsWithin5Min: d.redDecisions.map(
        (r) => `H+${min(r.t)}m ${String(r.payload.reason ?? 'decision')}`,
      ),
    };
  });

  for (const lt of extra.lostTimeFull) for (const id of lt.eventIds) known.add(id);

  const byCard: Record<string, number> = {};
  for (const o of ctx.orders) {
    if (o.side !== 0) continue;
    const label = extra.cardLabel(o.card);
    byCard[label] = (byCard[label] ?? 0) + 1;
  }
  const blueOrders = ctx.orders.filter((o) => o.side === 0).sort((a, b) => a.issuedAt - b.issuedAt);

  const facts: Facts = {
    outcome: extra.outcome,
    durationMin: extra.durationMin,
    stats: ctx.stats,
    decisions,
    lostTime: extra.lostTimeFull,
    orderSummary: {
      total: blueOrders.length,
      byCard,
      firstOrderMin: blueOrders.length > 0 ? min(blueOrders[0].issuedAt) : null,
      lastOrderMin: blueOrders.length > 0 ? min(blueOrders[blueOrders.length - 1].issuedAt) : null,
    },
  };

  return {
    system: DEBRIEF_SYSTEM,
    user: JSON.stringify(facts),
    knownEventIds: known,
  };
}

/** The timeout the narrative call gets, from `VITE_LLM_TIMEOUT_MS` (default 6000 ms, spec §6). */
/**
 * How long the rapporteur gets.
 *
 * Longer than anything in the fight, and deliberately so: `VITE_LLM_TIMEOUT_MS` is sized for a live
 * call the player is waiting on mid-battle, while this one is six cited paragraphs written from the
 * whole event log, asked for once, with the reader already busy in the order log and the replay above
 * it. Measured at 30 s the model was still writing when the clock cut it off and the template stood in;
 * the floor below is what makes the model's narrative the one the debrief usually shows.
 */
const DEBRIEF_FLOOR_MS = 75_000;

export function debriefTimeoutMs(): number {
  const raw = import.meta.env?.VITE_LLM_TIMEOUT_MS;
  const n = Number(raw);
  return Math.max(DEBRIEF_FLOOR_MS, Number.isFinite(n) && n > 0 ? n : 6000);
}
